import { useCallback, useMemo } from 'react'
import { useToastContext } from '../../../context/ToastContext'
import { TOAST_TYPES } from './ToastRegistry'

/**
 * Public hook for showing toasts from any component.
 *
 * Basic usage:
 *   const toast = useToast()
 *   toast.success('Saved!')
 *   toast.error('Failed to save')
 *
 * Advanced usage:
 *   const id = toast.success('Processing...', { duration: 10000 })
 *   toast.hide(id)  // Cancel specific toast
 *   toast.clearAll() // Clear all toasts
 *
 * @returns {Object} Toast API with convenience methods and state inspection
 */
export function useToast() {
    const { show, hide, clearAll, toasts, queue } = useToastContext()

    const success = useCallback(
        (message, options) => show(message, TOAST_TYPES.SUCCESS, options),
        [show]
    )

    const error = useCallback(
        (message, options) => show(message, TOAST_TYPES.ERROR, options),
        [show]
    )

    const warning = useCallback(
        (message, options) => show(message, TOAST_TYPES.WARNING, options),
        [show]
    )

    const info = useCallback((message, options) => show(message, TOAST_TYPES.INFO, options), [show])

    return useMemo(
        () => ({
            // Convenience methods (most common usage)
            success,
            error,
            warning,
            info,

            // Generic show with full control
            show,

            // Dismissal
            hide,
            dismiss: hide, // Alias
            clearAll,

            // State inspection (useful for debugging)
            activeCount: Object.keys(toasts).length,
            queueCount: queue.length,
            hasToasts: Object.keys(toasts).length > 0,
        }),
        [success, error, warning, info, show, hide, clearAll, toasts, queue]
    )
}

export default useToast
